import { Container, Button } from "@brightagency/headstart-common";
import SearchIcon from '@mui/icons-material/Search';
import React, { FC, useMemo, useRef, useEffect } from "react";
import { WPPost } from "../../../constants/types";
import {
    ChallengingSituationsACF,
    ConditionPostTypeACF,
    ListingAcf,
    PopulationPostTypeACF,
    CorePostTypesAcf,
} from "../../../constants/types/acf";
import { useGlobalLinks, useFreeServiceVisibility, useCurrentRouteData } from "../../../services/app-state";
import { filterListingsByCost, filterListingsByRadius } from "../../../services/app-state/services";
import { useListingSorters, getAcfPostUrl } from "../../../utils";
import { CommunityContext } from "../../../utils/community-context";
import { useSortingCoords } from "../../../utils/hooks";
import { getPostType, PostTypes } from "../../../utils/post-type-page-utils";
import { Separator } from "../../atoms/Separator";
import { SortButton } from "../../atoms/SortButton";
import { useNSWTBProcessLocalServices } from "../../organisms/AssessmentScreens/services";
import { RelatedListings } from "../../organisms/RelatedListings";
import { ServicesInRegionListings } from "../../organisms/ServicesInRegionListings";
import {
    SortButtonWrapper
} from "./styled";

const BaseRelatedListings: React.FC<{
    post: WPPost<PopulationPostTypeACF | ConditionPostTypeACF | ChallengingSituationsACF>;
    listings: WPPost<ListingAcf>[] | undefined;
    isLoading: boolean;
}> = ({ listings, isLoading }) => {
    const { locationSort } = useListingSorters();
    const sortCoords = useSortingCoords();
    const [showingFreeService] = useFreeServiceVisibility();

    const filteredListings = useMemo(() => filterListingsByRadius(listings, sortCoords), [listings, sortCoords]);
    const freeListing = useMemo(() => filterListingsByCost(filteredListings, showingFreeService), [filteredListings, showingFreeService]);
    const sortedListings = useMemo(() => freeListing.sort(locationSort), [freeListing, locationSort]);

    return (
        <RelatedListings
            title="Services near"
            description=""
            items={sortedListings}
            isLoading={isLoading}
        />
    );
};

const RegionalServiceRelatedListings: React.FC<{
    post: WPPost<PopulationPostTypeACF | ConditionPostTypeACF | ChallengingSituationsACF>;
    listings: WPPost<ListingAcf>[] | undefined;
    title: string;
    pagename: string;
    region: boolean;
    compact: boolean;
    isLoading?: boolean;
}> = ({ listings, title, pagename, region, compact, isLoading }) => {
    const [showingFreeService] = useFreeServiceVisibility();
    const sortedListings = useMemo(() => filterListingsByCost(listings ?? [], showingFreeService), [listings, showingFreeService]);

    if (isLoading) {
        return null;
    }
    
    return (
        <ServicesInRegionListings displayRegion={region} title={title} pagename={pagename} description='' items={sortedListings} compact={compact} />
    );
};

const NSWTBRelatedListings: React.FC<{
    post: WPPost<PopulationPostTypeACF | ConditionPostTypeACF | ChallengingSituationsACF>;
}> = ({ post }) => {
    const page = useCurrentRouteData<CorePostTypesAcf>();
    const preferredTitle = page.acf.meta?.sentence_case_title ?? page.title.rendered;
    const assessmentData = post.acf.local_services;
    const { data, isError, isLoading } = useNSWTBProcessLocalServices(assessmentData);
    const [, { getGlobalLink }] = useGlobalLinks();
    const communities = useMemo(() => (post.acf.local_services?.communities ?? []), [post.acf.local_services?.communities]);

    const ref = useRef<HTMLDivElement>(null);
    const scrollToElement = () => {
        ref.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (window.location.hash == '#suburbSelected') {
            scrollToElement();
        }
    }, []);

    if (isError) {
        return <p>An error occurred</p>;
    }
    
    return (
        <div ref={ref}>
            <Separator />
            <Container style={{ marginTop: "20px" }}>
                <h2>Services for {preferredTitle}</h2>
            </Container>
            <SortButtonWrapper>
                <SortButton />
            </SortButtonWrapper>
            <CommunityContext.Provider value={communities}>
                <BaseRelatedListings isLoading={isLoading} post={post} listings={data?.general} />
                <RegionalServiceRelatedListings isLoading={isLoading}  post={post} listings={data?.regional_service} title='Services available in' pagename='local' region={true} compact={false} />
                <RegionalServiceRelatedListings isLoading={isLoading}  post={post} listings={data?.digital_service} title='Phone and online services' pagename='local' region={false} compact={true} />
            </CommunityContext.Provider>
            <Container style={{ textAlign: "center", paddingTop: '20px', paddingBottom: '20px' }}>
                <Button
                    className="search-services"
                    to={getAcfPostUrl(getGlobalLink("local_services_page"))}
                    appearance="secondary"
                >
                    <SearchIcon />
                    Search all services
                </Button>
            </Container>
        </div>
    );
};

export const RelatedLocalServices: FC<{ post: WPPost<CorePostTypesAcf> }> = ({ post }) => {
    if (getPostType(post) === PostTypes.LocalInformation) {
        return null;
    }
    
    if( post.acf.nswtb_process_local_services === false ) {
        return null;
    }
    
    return <NSWTBRelatedListings post={post} />;
};
